import { callAuthorizedGraphQLEndpoint } from '../../../../Module/graphQL'
import { callAuthorizedGraphQLEndpoint as callAuthorizedPathfinderGraphQLEndpoint } from '../../../../Module/graphQL/PathFinder/pathFinderGQL'

/**
 * Gets the data for master schedule screen export.
 * @param {number} campaignId
 */
export const getMasterScheduleData = async ({ campaignId, ...params }) => {
  const includeInventory = true
  const query = `query 
  (
    $campaignId:ID!
    $first: Int
    $after: String
    $locationIds:[ID]
    $cityIds:[ID]
    $statusAndAboves: [MissionStatusEnum]
    $statusAndBelows: [MissionStatusEnum]
    $timeOfDays: [MissionTimeOfDayEnum]
    $filter:String
    $orderBy:MissionOrderByEnum
    $dayOfTheWeeks:[DayOfTheWeekEnum]
    $startDates:[String]
    $endDates:[String]
    $includeInventory:Boolean
    $noShow: Boolean
    ) {
      campaign(id:$campaignId){
        country{
          currencySign
          code
        }
        missions(
          first: $first,
          after: $after
          locationIds: $locationIds
          statusAndAboves: $statusAndAboves
          statusAndBelows: $statusAndBelows
          timeOfDays: $timeOfDays
          filter: $filter
          orderBy: $orderBy
          dayOfTheWeeks: $dayOfTheWeeks
          startDates: $startDates
          endDates: $endDates
          cityIds: $cityIds
          includeInventory: $includeInventory
          noShow: $noShow
          ) {
          totalCount
          edges {
            node {
              id
              flyerCode
              status
              totalFlyersDistributed
              dateTime
              cancelled
              payoutRatio
              distributionCost
              packSize
              printingCostPerFlyer
              bonusPerConversion
              bonusRedemptionPeriod
              type
              timeOfDay
              isNotShown
              zipCode
              flyerCodeV2 {
                tag
                code
              }
              cityPlan {
                city {
                  id
                  name
                  lunchCheckInTime
                  afternoonCheckInTime
                  timezone
                }
              }
              user {
                id
                firstName
                lastName
              }
              location {
                id
                name
                type {
                  name
                }
              }
              metricResults {
                nodes {
                  id
                  value
                  metric {
                    id
                    label
                    name
                  }
                }
              }
              conversions {
                 nodes {
                    id
                    timestamp
                    metric {
                      isPrimary
                    }
                 }
              }
              label
              amountScans
              spyInformation{
                spy{
                  id
                  firstName
                  lastName
                }
                spyDetails{
                  checkInTime
                  checkoutTime
                  reportReason
                  }
                shift{
                  id
                }
              }
            }
          }
        }
        cityPlans {
          nodes {
            id
            city {
              id
              name
              lunchCheckInTime
              afternoonCheckInTime
              timezone
              locations {
                edges {
                  node {
                    id
                    name
                    isSelfServe
                    campaignId
                    status
                    type {
                      name
                    }
                  }
                }
              }
            }
          }
        }
        artworksLean {
          id
          name
          inventoryCount
        }
      }
    }`

  const response = await callAuthorizedGraphQLEndpoint(query, {
    campaignId,
    ...params,
    includeInventory,
  })
  return response.json()
}

/**
 * Gets the data for master schedule missions import.
 * @param {number} campaignId
 */
export const getDataForMissionsImport = async ({ campaignId, cityPlanId }) => {
  const query = `query ($campaignId:ID!, $cityPlanId: ID) {
      campaign(id:$campaignId){
        cityPlans(cityPlanId: $cityPlanId) {
          nodes {
            city {
              brandAmbassadors {
                edges {
                  node {
                    id
                    firstName
                    lastName
                  }
                }
              }
            }
          }
        }
      }
    }`

  const response = await callAuthorizedGraphQLEndpoint(query, {
    campaignId,
    cityPlanId,
  })
  return response.json()
}

export const gqlCampaign = `query ($campaignId:ID!) {
    campaign(id:$campaignId){
      cityPlans{
        nodes {
           id
        }
      }
    }
  }`

export const gqlCityPlans = `
  query ($cityPlanId:ID!) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges {
        node {
          id
          city {
            name
            lunchCheckInTime
            afternoonCheckInTime
            country {
              code
            }
          }
          permitCosts {
            nodes {
              startDate
              cost
            }
          }
        }
      }
    }
  }
  `

export const gqlMissions = `
   query ($cityPlanId:ID!, $missionStatuses: [MissionStatusEnum], $first: Int, $skip: Int, $includeInventory: Boolean) {
    cityPlans(cityPlanId: $cityPlanId) {
      edges {
        node {
           id
            missions(statuses: $missionStatuses, first: $first, skip: $skip, includeInventory: $includeInventory) {
              pageInfo {
                hasNextPage
              }  
              edges {
                node {
                  id
                  flyerCode
                  status
                  totalFlyersDistributed
                  dateTime
                  distributionCost
                  printingCostPerFlyerV2
                  packSize
                  bonusPerConversion
                  bonusRedemptionPeriod
                  type
                  boundary {
                    latitude
                    longitude
                  }
                  flyerCodeV2 {
                    tag
                    code
                  }
                  user {
                    firstName
                    lastName
                  }
                  location {
                    id
                    name
                    hasPermit
                    latitude
                    longitude
                    type {
                      name
                    }
                  }
                  metricResults {
                    nodes {
                      id
                      value
                      metric {
                        id
                        label
                        name
                      }
                    }
                  }
                  conversions {
                    nodes {
                      id
                      timestamp
                      metric {
                        isPrimary
                      }
                    }
                  }
                  label
                  amountScans
                  spyInformation{
                    spy{
                      firstName
                      lastName
                    }
                  }
                  surgePayout
                  checkInTime
                  checkoutTime
                }
              }
            }
        }
      }
    }
  }
  `

const getZipCodesForMissions = async ({ countryCode, data }) => {
  const query = `query zipCodes($input: ZipCodesInput!) {
  zipCodes(input: $input) {
    id
    zipCode
  }
}`

  const response = await callAuthorizedPathfinderGraphQLEndpoint(query, {
    input: {
      countryCode,
      data,
    },
  })
  const result = await response.json()

  return result.data.zipCodes
}

export const getZipCodesByChunks = async ({ chunks, countryCode }) => {
  const zipCodes = []

  for (const data of chunks) {
    const result = await getZipCodesForMissions({ countryCode, data })
    zipCodes.push(...result)
  }

  return zipCodes
}
